import React, { useState, useRef, useEffect } from 'react';
import Select, { components } from 'react-select';
import cn from 'classnames';
import style from './index.module.scss';
import ExpandMore from '@assets/images/Fireboard/ExpandMore';

const MultiSelect = ({
  options = [],
  value = [],
  placeholder = 'Please select',
  onChange = () => {},
  width = 20,
  isMulti = false,
  maxToShow,
  isSearchable = false,
  ...props
}) => {
  const [count, setCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = e => {
      // Ignore clicks/taps inside the react-select portaled menu (rendered outside wrapperRef)
      const touch = e.touches?.[0] || e.changedTouches?.[0];
      const target = touch ? document.elementFromPoint(touch.clientX, touch.clientY) : e.target;
      if (
        target?.closest('.react-select__menu-portal') ||
        target?.closest('.react-select__menu')
      ) {
        return;
      }
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [menuOpen]);

  const handleChange = selectedOptions => {
    const overflowCount = (selectedOptions?.length || 0) - maxToShow;
    setCount(overflowCount > 0 ? overflowCount : 0);
    onChange(selectedOptions);
  };

  const customMultiValue = props => {
    const { index } = props;
    if (index >= maxToShow) return null;
    return <components.MultiValue {...props} />;
  };
  const customMultiValueLabel = props => {
    return (
      <components.MultiValueLabel {...props}>
        <span className={style.pillLabel}>{props.data.label}</span>
      </components.MultiValueLabel>
    );
  };

  // Stops mousedown/touchend from bubbling up to ControlOverride so that
  // clicking × never accidentally toggles the dropdown.
  const customMultiValueRemove = props => {
    return (
      <components.MultiValueRemove
        {...props}
        innerProps={{
          ...props.innerProps,
          onMouseDown: e => {
            e.stopPropagation();
            props.innerProps?.onMouseDown?.(e);
          },
          onTouchEnd: e => {
            e.preventDefault();
            e.stopPropagation();
            props.innerProps?.onTouchEnd?.(e);
          },
        }}
      />
    );
  };
  const SingleValue = props => {
    return (
      <components.SingleValue {...props}>
        <span className={style.singleValue}>{props.data.label}</span>
      </components.SingleValue>
    );
  };
  const customOptions = props => {
    const { isSelected, label } = props;

    return (
      <components.Option {...props}>
        <div className={cn('d-flex align-items-center')}>
          <div
            className={cn(style.customCheckbox, {
              [style.checked]: isSelected,
            })}
          />
          <span className={style.optionLabel}>{label}</span>
        </div>
      </components.Option>
    );
  };

  const ControlOverride = props => {
    const { innerProps = {}, isDisabled } = props;

    const handleToggle = e => {
      if (isDisabled) return;

      // For touchend, `touches` is always empty — use changedTouches for the released touch point
      const touch = e.changedTouches?.[0];
      const target = touch
        ? document.elementFromPoint(touch.clientX, touch.clientY)
        : e.target;

      const isInput = target?.tagName?.toLowerCase() === 'input';
      const isClearIcon = target?.closest('.react-select__clear-indicator');

      if (isInput) return;

      if (isClearIcon) {
        setMenuOpen(false);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setMenuOpen(prev => !prev);
    };

    return (
      <components.Control
        {...props}
        innerProps={{
          ...innerProps,
          onMouseDown: handleToggle,
          onTouchEnd: handleToggle,
        }}
      />
    );
  };

  const customExpand = props => {
    return (
      <components.DropdownIndicator {...props}>
        <div className={style.ExpandIcon}>
          <ExpandMore stroke="var(--primary-icon-color)" />
        </div>
      </components.DropdownIndicator>
    );
  };

  const customStyles = {
    container: (base, state) => ({
      ...base,
      height: '100%',
      width: '100%',
    }),
    control: (base, state) => ({
      ...base,
      height: '100%',
      border: 'none',
      backgroundColor: 'var(--multiselect-bg)',
      borderRadius: '0.5rem',
      color: 'var(--tertiary-text)',
      fontWeight: 400,
      paddingLeft: '4px',
      boxShadow: 'none',
      '&:hover': {
        border: 'none',
      },
    }),
    input: base => ({
      ...base,
      color: 'white',
    }),
    valueContainer: (base, state) => ({
      ...base,
    }),
    placeholder: base => ({
      ...base,
      fontSize: '1rem',
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: 'white',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
      transition: 'transform 0.2s ease',
      '&:hover': {
        color: 'white',
      },
    }),
    placeholder: base => ({
      ...base,
      color: 'var(--tertiary-text)',
    }),
    multiValue: base => ({
      ...base,
      backgroundColor: 'transparent',
      borderRadius: '8px',
      border: '1px solid var(--multiselect-value-pill-border)',
      '& div': {
        '&:nth-child(2)': {
          paddingLeft: '0px',
        },
      },
    }),
    multiValueLabel: base => ({
      ...base,
      color: 'var(--tertiary-text)',
    }),
    multiValueRemove: base => ({
      ...base,
      color: 'var(--secondary-icon-color)',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.2)',
        color: 'var(--secondary-icon-color)',
      },
      svg: {
        width: '20px',
        height: '20px',
      },
    }),
    menu: base => ({
      ...base,
      zIndex: 9999,
      backgroundColor: 'var(--multiSelect-menu-bg)',
      border: '2px solid var(--multiSelect-menu-border)',
      boxShadow:
        '0px 3px 9px 3px rgba(0, 0, 0, 0.15), 0px 1.5px 3px 0px rgba(0, 0, 0, 0.30)',
      borderRadius: '12px',
      '& > div': {
        borderRadius: '12px',
        '& > div:first-child': {
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
        },
        '& > div:last-child': {
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
        },
      },
    }),
    option: (base, state) => ({
      ...base,
      height: '100%',
      backgroundColor: state.isSelected
        ? 'var(--multiSelect-menu-bg)'
        : state.isFocused
        ? 'var(--multiSelect-menu-bg)'
        : 'var(--multiSelect-menu-bg)',
      color: 'var(--primary-text)',
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
    }),
  };
  return (
    <div ref={wrapperRef} className="h-100 position-relative d-flex align-items-center">
      <Select
        isMulti={isMulti}
        menuIsOpen={menuOpen}
        openMenuOnClick={true}
        options={options}
        closeMenuOnSelect={!isMulti}
        hideSelectedOptions={false}
        blurInputOnSelect={!isMulti}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: customExpand,
          Control: ControlOverride,
          ...(isMulti
            ? {
                Option: customOptions,
                MultiValue: customMultiValue,
                MultiValueLabel: customMultiValueLabel,
                MultiValueRemove: customMultiValueRemove,
              }
            : {
                SingleValue: SingleValue,
              }),
        }}
        value={value}
        onChange={handleChange}
        width={width}
        placeholder={placeholder}
        styles={customStyles}
        isClearable={!isMulti}
        isSearchable={isSearchable}
      />
      {count > 0 && isMulti && value.length > 0 && (
        <span className={cn('position-absolute fs_14', style.countLabel)}>
          +{count}
        </span>
      )}
    </div>
  );
};

export default MultiSelect;
